import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  Search,
  Filter,
  Clock,
  Users,
  Star,
  BookOpen,
  Play,
  ChevronRight,
  Calendar,
  Award,
  DollarSign
} from 'lucide-react'

const COURSES = [
  { id: 1, slug: "linux-and-networking-essentials", title: "Linux & Networking Essentials", price: 8000, duration: "30 hrs", category: "Networking", level: "Beginner", description: "Master Linux command line, system administration, and core networking concepts including TCP/IP, DNS and routing." },
  { id: 2, slug: "cloud-essentials", title: "Cloud Essentials", price: 8000, duration: "20 hrs", category: "Cloud", level: "Beginner", description: "Get started with AWS, GCP and Azure cloud platforms and core services." },
  { id: 3, slug: "devops-essentials", title: "DevOps Essentials", price: 10000, duration: "30 hrs", category: "DevOps", level: "Intermediate", description: "CI/CD pipelines, Docker, Kubernetes and modern DevOps practices." },
  { id: 4, slug: "aws-professional-architect", title: "AWS Professional Architect", price: 15000, duration: "60 hrs", category: "Cloud", level: "Advanced", description: "Design and deploy scalable, highly available systems on AWS." },
  { id: 5, slug: "gcp-professional-architect", title: "GCP Professional Architect", price: 15000, duration: "60 hrs", category: "Cloud", level: "Advanced", description: "Architect enterprise-grade solutions on Google Cloud Platform." },
  { id: 6, slug: "azure-architect", title: "Azure Architect", price: 15000, duration: "60 hrs", category: "Cloud", level: "Advanced", description: "Design robust cloud solutions and infrastructure on Microsoft Azure." },
  { id: 7, slug: "kubernetes-architect", title: "Kubernetes Architect", price: 20000, duration: "40 hrs", category: "DevOps", level: "Advanced", description: "Go from running containers to owning the platform — master Kubernetes at production scale." },
];

const CATEGORY_COLORS = {
  Networking: "bg-blue-100 text-blue-700",
  Cloud: "bg-emerald-100 text-emerald-700",
  DevOps: "bg-orange-100 text-orange-700",
};

const ACCENT_BAR_COLORS = {
  Networking: "bg-blue-500",
  Cloud: "bg-emerald-500",
  DevOps: "bg-orange-500",
};

const Courses = () => {
  const courses = COURSES
  const [filteredCourses, setFilteredCourses] = useState(COURSES)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    if (!searchTerm) {
      setFilteredCourses(COURSES)
    } else {
      const lower = searchTerm.toLowerCase()
      setFilteredCourses(COURSES.filter(course => course.title.toLowerCase().includes(lower)))
    }
  }, [searchTerm])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-700 text-white section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Explore Our Courses
            </h1>
            <p className="text-xl text-primary-100 mb-8">
              Choose from our wide range of industry-recognized courses and
              start your journey to becoming a DevOps expert
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white dark:bg-gray-800 dark:border-gray-700"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {filteredCourses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No courses found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Try adjusting your search criteria
              </p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredCourses.map((course) => (
                <motion.div
                  key={course.id}
                  variants={itemVariants}
                  className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow overflow-hidden flex flex-col"
                >
                  {/* Top accent bar */}
                  <div className={`h-1 w-full ${ACCENT_BAR_COLORS[course.category]}`} />

                  {/* Card body */}
                  <div className="p-6 flex flex-col gap-3 flex-grow">
                    {/* Row 1: category pill + level pill + duration pill */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-semibold rounded-full px-3 py-1 ${CATEGORY_COLORS[course.category]}`}>
                        {course.category}
                      </span>
                      {course.level && (
                        <span className="text-xs font-semibold rounded-full px-3 py-1 bg-gray-100 text-gray-600">
                          {course.level}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1 bg-gray-100 text-gray-600">
                        <Clock className="w-3 h-3" />
                        {course.duration}
                      </span>
                    </div>

                    {/* Row 2: title */}
                    <h3 className="text-xl font-bold text-gray-900">
                      {course.title}
                    </h3>

                    {/* Row 3: description */}
                    <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
                      {course.description}
                    </p>

                    {/* Divider + bottom row */}
                    <div className="border-t border-gray-100 mt-auto pt-4 flex items-center justify-between">
                      <span className="text-2xl font-bold text-gray-900">
                        ₹{course.price.toLocaleString('en-IN')}
                      </span>
                      <Link
                        to={`/courses/${course.slug}`}
                        className="text-sm text-green-700 hover:underline font-medium"
                      >
                        View Details →
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center text-white max-w-3xl mx-auto"
          >
            <h2 className="text-3xl font-bold mb-4">
              Can't Find What You're Looking For?
            </h2>
            <p className="text-xl mb-8 text-primary-100">
              Get in touch with our course advisors to find the perfect program for your career goals
            </p>
            <Link to="/contact" className="btn bg-white text-primary-600 hover:bg-gray-50 btn-lg">
              Talk to an Advisor
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Courses
